import { create } from "zustand";

interface ModalSequenceState {
	activeModal: "email" | "relationship" | null;
	/** Session guards — prevent re-showing modals within a session */
	hasShownEmail: boolean;
	hasShownRelationship: boolean;
	currentUserId: string | null;

	// Derived convenience getters are computed via selectors, but we store activeModal
	// and expose actions:
	completeEmailModal: (userHasGroups: boolean) => void;
	completeRelationshipModal: () => void;

	/**
	 * Evaluate the modal sequence based on current auth/user state.
	 * Called by the ModalSequenceHydrator whenever auth state changes.
	 */
	evaluate: (params: {
		isAuthenticated: boolean;
		userId: string | null;
		hasEmails: boolean;
		hasGroups: boolean;
	}) => void;

	/** Reset session state (e.g. on logout). */
	reset: () => void;
}

export const useModalSequenceStore = create<ModalSequenceState>((set, get) => ({
	activeModal: null,
	hasShownEmail: false,
	hasShownRelationship: false,
	currentUserId: null,

	evaluate: ({ isAuthenticated, userId, hasEmails, hasGroups }) => {
		const state = get();

		if (!isAuthenticated || !userId) {
			// User logged out — reset everything
			set({
				activeModal: null,
				hasShownEmail: false,
				hasShownRelationship: false,
				currentUserId: null,
			});
			return;
		}

		// Reset session guards if user changed
		let { hasShownEmail, hasShownRelationship } = state;
		if (state.currentUserId !== userId) {
			hasShownEmail = false;
			hasShownRelationship = false;
		}

		// Sequential logic:
		// 1. No emails && not shown email dialog -> show email
		// 2. Has emails, no groups && not shown relationship dialog -> show relationship
		// 3. Has both -> null
		let activeModal: "email" | "relationship" | null = null;
		if (!hasEmails && !hasShownEmail) {
			activeModal = "email";
		} else if (hasEmails && !hasGroups && !hasShownRelationship) {
			activeModal = "relationship";
		}
		if (hasEmails && hasGroups) {
			activeModal = null;
		}

		set({
			activeModal,
			hasShownEmail,
			hasShownRelationship,
			currentUserId: userId,
		});
	},

	completeEmailModal: (userHasGroups: boolean) => {
		set({
			hasShownEmail: true,
			activeModal: userHasGroups ? null : "relationship",
		});
	},

	completeRelationshipModal: () => {
		set({
			hasShownRelationship: true,
			activeModal: null,
		});
	},

	reset: () => {
		set({
			activeModal: null,
			hasShownEmail: false,
			hasShownRelationship: false,
			currentUserId: null,
		});
	},
}));
